import { BankStatementStatus, BankStatementTransactionStatus, ReconciliationLogStatus } from '@/models/back-office-pages/bank-statements'
import {
  ClientDebtorRelationshipStatus,
  ClientDebtorStatus,
  ContractStatus,
  DebtorTaskStatus,
  FABClientStatus,
  SettlementStatus,
} from '@/models/back-office-pages/clients'
import { TaskAssignmentStatus, TaskInvoiceStatus } from '@/models/back-office-pages/debt-collection'
import { InvestmentTaskDocumentStatus, InvestorStatus, OfferStatus, ReinvestmentStatus } from '@/models/back-office-pages/investment'
import {
  InvoiceExternalApprovalStatus,
  InvoiceExternalFilterApprovalStatus,
  InvoiceExternalVerificationStatusFilter,
  InvoiceReportStatus,
  InvoiceStatus,
  PeppolSentStatus,
} from '@/models/back-office-pages/invoices'
import {
  LoanBookProductStatus,
  LoanBookVatNoteStatus,
  PaymentScheduleStatus,
  PurchaseNoteStatus,
} from '@/models/back-office-pages/loan-book'
import { AccountingNoteStatus, PayoutStatus } from '@/models/back-office-pages/notes'
import { InvoiceBulkVerificationStatus, OutstandingVerificationStatus } from '@/models/back-office-pages/operation-tasks'
import { AccountingNoteReportStatus } from '@/models/back-office-pages/payments-reports'
import { ApprovalStatus, DebtorStatus, KycQuestionnaireStatus, KycRisk, ProspectStatus } from '@/models/back-office-pages/prospects'
import { StoplistCompanyMatchType, StoplistPersonMatchType, StoplistStatus } from '@/models/back-office-pages/stop-list'
import { ClientStatus } from '@/models/back-office-pages/system'
import { ClarificationStatus, Status, YesNo } from '@/models/common'
import { StatusColor } from '@/styles'

export const statusColorMap = {
  [ProspectStatus.APPROVED]: StatusColor.GREEN,
  [ApprovalStatus.APPROVED_WITH_PROSPECT]: StatusColor.GREEN,
  [YesNo.YES]: StatusColor.GREEN,
  [KycQuestionnaireStatus.SUCCESSFUL]: StatusColor.GREEN,
  [StoplistStatus.ACTIVE]: StatusColor.GREEN,
  [FABClientStatus.APPROVED]: StatusColor.GREEN,
  [TaskInvoiceStatus.CLOSED]: StatusColor.GREEN,
  [ClientDebtorStatus.ACTIVE]: StatusColor.GREEN,
  [InvoiceStatus.READY_TO_BUY]: StatusColor.GREEN,
  [InvoiceStatus.PAID]: StatusColor.GREEN,
  [InvoiceExternalApprovalStatus.EXTERNAL_VERIFICATION_NOT_REQUIRED]: StatusColor.GREEN,
  [InvoiceExternalApprovalStatus.EXTERNAL_VERIFICATION_APPROVED_BY_SA]: StatusColor.GREEN,
  [InvoiceExternalApprovalStatus.EXTERNAL_VERIFICATION_APPROVED_BY_DEBTOR]: StatusColor.GREEN,
  [SettlementStatus.PARTIALLY_PAID]: StatusColor.GREEN,
  [SettlementStatus.INVOICED]: StatusColor.GREEN,
  [ClientStatus.EMAIL_CONFIRMED]: StatusColor.GREEN,
  [AccountingNoteStatus.PAID]: StatusColor.GREEN,
  [InvoiceStatus.CLOSED]: StatusColor.GREEN,
  [InvoiceReportStatus.READY]: StatusColor.GREEN,
  [DebtorTaskStatus.COMPLETED]: StatusColor.GREEN,
  [TaskAssignmentStatus.DONE]: StatusColor.GREEN,
  [ReinvestmentStatus.FINISHED]: StatusColor.GREEN,
  [InvestmentTaskDocumentStatus.SIGNED]: StatusColor.GREEN,
  [InvoiceExternalVerificationStatusFilter.APPROVED_BY_SA]: StatusColor.GREEN,
  [InvoiceExternalFilterApprovalStatus.EXTERNAL_VERIFICATION_APPROVED]: StatusColor.GREEN,
  [InvoiceBulkVerificationStatus.VERIFIED]: StatusColor.GREEN,
  [PeppolSentStatus.SENT_SUCCESS]: StatusColor.GREEN,

  [InvoiceStatus.FUNDED]: StatusColor.YELLOW_GREEN,
  [LoanBookVatNoteStatus.PARTLY_PAID]: StatusColor.YELLOW_GREEN,
  [InvestorStatus.RISK_APPROVED]: StatusColor.YELLOW_GREEN,
  [InvestmentTaskDocumentStatus.SENT_TO_INVESTOR]: StatusColor.YELLOW_GREEN,
  [ContractStatus.RENEWED]: StatusColor.YELLOW_GREEN,
  [InvoiceExternalVerificationStatusFilter.APPROVED_BY_DEBTOR]: StatusColor.YELLOW_GREEN,

  [ProspectStatus.NEW]: StatusColor.VIOLET,
  [FABClientStatus.INIT]: StatusColor.VIOLET,
  [StoplistCompanyMatchType.PROSPECT_COMPANY]: StatusColor.VIOLET,
  [StoplistCompanyMatchType.ANNUAL_REVIEW_COMPANY]: StatusColor.VIOLET,
  [StoplistPersonMatchType.PROSPECT_CONTACT_PERSON]: StatusColor.VIOLET,
  [SettlementStatus.NOT_PAID]: StatusColor.VIOLET,
  [ClientStatus.REGISTRATION_INITIATED]: StatusColor.VIOLET,
  [ClientStatus.WAITING_FOR_APPROVAL]: StatusColor.VIOLET,
  [ClientDebtorRelationshipStatus.INIT]: StatusColor.VIOLET,
  [PayoutStatus.PREPARED]: StatusColor.VIOLET,
  [PaymentScheduleStatus.CONSTRUCTED]: StatusColor.VIOLET,
  [ReinvestmentStatus.NOT_SIGNED]: StatusColor.VIOLET,
  [InvestmentTaskDocumentStatus.NOT_GENERATED]: StatusColor.VIOLET,
  [PeppolSentStatus.NOT_SENT]: StatusColor.VIOLET,

  [ProspectStatus.REJECTED]: StatusColor.RED,
  [KycQuestionnaireStatus.FAILED]: StatusColor.RED,
  [KycRisk.HIGH]: StatusColor.RED,
  [ClientStatus.BLOCKED]: StatusColor.RED,
  [ClientStatus.BLOCKED_INITIATED]: StatusColor.RED,
  [ClientStatus.BLOCKED_EMAIL_CONFIRMED]: StatusColor.RED,
  [ClientDebtorStatus.BLOCKED]: StatusColor.RED,
  [ContractStatus.EXPIRED]: StatusColor.RED,
  [TaskInvoiceStatus.OVERDUE]: StatusColor.RED,
  [ClientDebtorRelationshipStatus.RELATIONSHIP_CLOSED]: StatusColor.RED,
  [InvoiceStatus.REJECTED]: StatusColor.RED,
  [InvoiceStatus.OVERDUE]: StatusColor.RED,
  [InvoiceStatus.DECLINED]: StatusColor.RED,
  [InvoiceExternalApprovalStatus.EXTERNAL_VERIFICATION_REJECTED]: StatusColor.RED,
  [InvoiceExternalApprovalStatus.DEBTOR_HAS_NOT_REPLIED]: StatusColor.RED,
  [InvoiceExternalApprovalStatus.EXTERNAL_VERIFICATION_REQUIRED]: StatusColor.RED,
  [InvoiceExternalApprovalStatus.DEBTOR_REJECTED_VERIFICATION]: StatusColor.RED,
  [InvoiceExternalApprovalStatus.VERIFICATION_SENT_TO_DEBTOR]: StatusColor.RED,
  [SettlementStatus.DELETED]: StatusColor.RED,
  [BankStatementStatus.CORRUPTED]: StatusColor.RED,
  [DebtorTaskStatus.NOT_STARTED]: StatusColor.RED,
  [AccountingNoteReportStatus.ERROR]: StatusColor.RED,
  [BankStatementTransactionStatus.DUPLICATE]: StatusColor.RED,
  [InvoiceStatus.WRITTEN_OFF]: StatusColor.RED,
  [AccountingNoteReportStatus.CANCELED]: StatusColor.RED,
  [PaymentScheduleStatus.NOT_CONSTRUCTED]: StatusColor.RED,
  [PurchaseNoteStatus.FAILED]: StatusColor.RED,
  [PeppolSentStatus.SENT_FAILURE]: StatusColor.RED,

  [ProspectStatus.WAITING_APPROVAL]: StatusColor.YELLOW,
  [ApprovalStatus.PENDING_RISK]: StatusColor.YELLOW,
  [ApprovalStatus.PENDING_WITH_PROSPECT]: StatusColor.YELLOW,
  [DebtorStatus.PENDING]: StatusColor.YELLOW,
  [DebtorStatus.APPROVAL_INITIATED]: StatusColor.YELLOW,
  [StoplistCompanyMatchType.DEBTOR_COMPANY]: StatusColor.YELLOW,
  [StoplistCompanyMatchType.ANNUAL_REVIEW_DEBTOR_COMPANY]: StatusColor.YELLOW,
  [StoplistPersonMatchType.DEBTOR_CONTACT_PERSON]: StatusColor.YELLOW,
  [ContractStatus.EXPIRING_SOON]: StatusColor.YELLOW,
  [SettlementStatus.ON_HOLD]: StatusColor.YELLOW,
  [InvoiceStatus.APPROVING]: StatusColor.YELLOW,
  [InvoiceStatus.SETTLING]: StatusColor.YELLOW,
  [LoanBookVatNoteStatus.SETTLED]: StatusColor.YELLOW,
  [LoanBookProductStatus.SETTLED]: StatusColor.YELLOW,
  [AccountingNoteStatus.PROCESSING]: StatusColor.YELLOW,
  [AccountingNoteStatus.QUEUED]: StatusColor.YELLOW,
  [PayoutStatus.SENT_TO_BANK]: StatusColor.YELLOW,
  [ClientDebtorRelationshipStatus.ADD_DEBTOR_REQUESTED]: StatusColor.YELLOW,
  [ClientDebtorRelationshipStatus.CLOSE_RELATIONSHIP_REQUESTED]: StatusColor.YELLOW,
  [ClientDebtorRelationshipStatus.NEW_CREDIT_LIMIT_REQUESTED]: StatusColor.YELLOW,
  [ClientDebtorRelationshipStatus.PROCESSING_BY_CLIENT]: StatusColor.YELLOW,
  [TaskInvoiceStatus.OPEN]: StatusColor.YELLOW,
  [OutstandingVerificationStatus.UNDER_INVESTIGATION]: StatusColor.YELLOW,
  [OutstandingVerificationStatus.SENT]: StatusColor.YELLOW,
  [PaymentScheduleStatus.SENT_TO_CLIENT]: StatusColor.YELLOW,
  [PaymentScheduleStatus.UPLOADED_SIGNED]: StatusColor.YELLOW,
  [PurchaseNoteStatus.PROCESSING]: StatusColor.YELLOW,
  [PurchaseNoteStatus.QUEUED]: StatusColor.YELLOW,
  [InvestorStatus.IN_RISK_APPROVAL]: StatusColor.YELLOW,
  [ReinvestmentStatus.BOOKED]: StatusColor.YELLOW,
  [InvestmentTaskDocumentStatus.GENERATED]: StatusColor.YELLOW,
  [InvoiceBulkVerificationStatus.IN_VERIFICATION]: StatusColor.YELLOW,
  [PeppolSentStatus.SENDING]: StatusColor.YELLOW,

  [ProspectStatus.NEEDS_CLARIFICATION]: StatusColor.ORANGE,
  [LoanBookProductStatus.NEEDS_CLARIFICATION]: StatusColor.ORANGE,
  [KycRisk.LOW]: StatusColor.ORANGE,
  [StoplistPersonMatchType.KYC]: StatusColor.ORANGE,
  [ReconciliationLogStatus.INVOICE_CLOSED]: StatusColor.ORANGE,
  [InvoiceStatus.SENT_TO_INSURANCE]: StatusColor.ORANGE,
  [InvoiceStatus.SENT_TO_LEGAL]: StatusColor.ORANGE,
  [OutstandingVerificationStatus.DEBTOR_RESPONDED]: StatusColor.ORANGE,
  [OfferStatus.FULLY_USED]: StatusColor.ORANGE,

  [YesNo.NO]: StatusColor.WHITE_GREY,
  [ProspectStatus.ARCHIVED]: StatusColor.WHITE_GREY,
  [ClarificationStatus.ANSWERED]: StatusColor.WHITE_GREY,
}

export const getStatusColor = (status: Status): StatusColor => statusColorMap[status] || StatusColor.GRAY
